import type { FormField } from "@tellescope/types-models";
import { advanceWhile, type FieldPathWalkContext } from "./walk-field-path";
import { getNextField } from "./get-next-field";
import { isPassThroughField, occupiesOwnPage, subFieldIdsOf } from "./question-group";
import type { FormFieldNode } from "./types";

export interface NextDisplayableField {
  /** The next field with UI to show, or `undefined` when the form has no further page */
  nextField: FormFieldNode | undefined;
  /**
   * Ids of the invisible fields stepped over on the way there — `Hidden Value`
   * fields and, for pass-through Question Groups, the group plus its sub-fields.
   * These must be marked as navigated so their answers are submitted.
   */
  skippedHiddenFieldIds: string[];
}

export interface ResolveNextDisplayableFieldArgs {
  activeField: FormFieldNode;
  /** All fields by id, used to resolve Question Group sub-field types */
  fieldsMap: Record<string, FormField | undefined>;
  /** Ids belonging to a Question Group, which render inside their parent rather than as a page */
  subFieldIds: ReadonlySet<string>;
  walkContext: FieldPathWalkContext;
}

/**
 * Finds the next field that actually renders, stepping over everything that doesn't.
 *
 * Sub-fields (shown inside their group) and pass-through fields (`Hidden Value`,
 * or a group made entirely of them) are skipped. If the walk runs out of path while
 * still on a non-displayable field there is no next page, but that trailing field is
 * still reported in `skippedHiddenFieldIds` so its answer is not dropped on submit.
 */
export const resolveNextDisplayableField = ({
  activeField,
  fieldsMap,
  subFieldIds,
  walkContext,
}: ResolveNextDisplayableFieldArgs): NextDisplayableField => {
  const first = getNextField(
    activeField,
    walkContext.responsesByFieldId[activeField.value.id],
    walkContext.allResponses,
    walkContext.logicOptions
  );
  if (!first) return { nextField: undefined, skippedHiddenFieldIds: [] };

  const { node, skipped } = advanceWhile(
    first,
    (candidate) => !occupiesOwnPage(candidate.value, subFieldIds, fieldsMap),
    walkContext
  );

  const isDisplayable = occupiesOwnPage(node.value, subFieldIds, fieldsMap);
  const skippedNodes = isDisplayable ? skipped : [...skipped, node];

  const skippedHiddenFieldIds = skippedNodes.flatMap((candidate) => {
    if (candidate.value.type === "Hidden Value") return [candidate.value.id];
    if (isPassThroughField(candidate.value, fieldsMap)) {
      return [candidate.value.id, ...subFieldIdsOf(candidate.value)];
    }
    return [];
  });

  return { nextField: isDisplayable ? node : undefined, skippedHiddenFieldIds };
};
